/** Eine Zeile des Tagesrasters: Name und Fenster, Schraffur, Blöcke und die gezogene Auswahl. */
import { useRef, useState, type PointerEvent } from 'react';
import type { ResourceOccupancyEntry, ResourceOccupancyRow } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { openingWindowLabel } from '../resourceModel';
import { ResourceGridBlock } from './ResourceGridBlock';
import {
  closedSpans,
  gridBlocks,
  selectionOf,
  slotAt,
  span,
  type GridAxis,
  type GridSelection,
} from './resourceGridModel';

export interface ResourceGridRowProps {
  axis: GridAxis;
  row: ResourceOccupancyRow;
  date: string;
  canManage: boolean;
  selection: GridSelection | null;
  onSelect: (selection: GridSelection) => void;
  onCancel: (entry: ResourceOccupancyEntry) => void;
}

export function ResourceGridRow({
  axis,
  row,
  date,
  canManage,
  selection,
  ...props
}: ResourceGridRowProps) {
  const track = useRef<HTMLDivElement>(null);
  // Der laufende Zug in einer Referenz: Ende und Bewegung lesen ihn, auch wenn noch nicht neu
  // gezeichnet wurde; der Zustand zeichnet nur die Markierung.
  const drag = useRef<{ anchor: number; current: number } | null>(null);
  const [dragging, setDragging] = useState<GridSelection | null>(null);
  const draggable = canManage && row.isActive;
  const minuteAt = (event: PointerEvent) => {
    const box = track.current!.getBoundingClientRect();
    return slotAt(axis, row, (event.clientX - box.left) / box.width);
  };
  const stop = () => {
    drag.current = null;
    setDragging(null);
  };
  const shown = dragging ?? selection;
  const marked = shown && span(axis, shown.startMinute, shown.endMinute);
  return (
    <div className="border-card-alt flex h-[58px] border-b">
      <div className="flex w-[180px] shrink-0 flex-col justify-center px-[18px]">
        <span className="truncate text-sm font-bold" title={row.name}>
          {row.name}
        </span>
        <span className="text-ink-soft truncate text-xs">
          {row.isActive ? openingWindowLabel(row) : `${openingWindowLabel(row)} · (deaktiviert)`}
        </span>
      </div>
      <div
        ref={track}
        className={cn('relative grow overflow-hidden', draggable && 'cursor-crosshair touch-none')}
        onPointerDown={(event) => {
          if (!draggable || event.button !== 0 || event.target !== event.currentTarget) return;
          // Mit Erfassung kommt das Ende des Zugs auch außerhalb der Zeile an. Ohne sie (ein
          // unbekannter Zeiger) beendet die nächste Bewegung ohne Taste den Zug.
          try {
            event.currentTarget.setPointerCapture?.(event.pointerId);
          } catch (failure) {
            if (!(failure instanceof DOMException)) throw failure;
          }
          const minute = minuteAt(event);
          drag.current = { anchor: minute, current: minute };
          setDragging(selectionOf(row.resourceId, minute, minute));
        }}
        onPointerMove={(event) => {
          if (!drag.current) return;
          if (event.buttons === 0) {
            stop();
            return;
          }
          const current = minuteAt(event);
          if (current === drag.current.current) return;
          drag.current = { ...drag.current, current };
          setDragging(selectionOf(row.resourceId, drag.current.anchor, current));
        }}
        onPointerUp={() => {
          if (drag.current) {
            props.onSelect(selectionOf(row.resourceId, drag.current.anchor, drag.current.current));
          }
          stop();
        }}
        // Abgebrochen (Kontextmenü, Bildlauf am Tablet) ist keine Auswahl.
        onPointerCancel={stop}
        onLostPointerCapture={stop}
      >
        {closedSpans(axis, row).map((closed) => (
          <span
            key={closed.left}
            aria-hidden
            className="pointer-events-none absolute inset-y-0 bg-[repeating-linear-gradient(135deg,var(--color-card-alt)_0_4px,transparent_4px_8px)]"
            style={{ left: `${closed.left}%`, width: `${closed.width}%` }}
          />
        ))}
        {gridBlocks(axis, row, date).map((block) => (
          <ResourceGridBlock
            key={block.entry.id}
            block={block}
            canManage={canManage}
            onCancel={props.onCancel}
          />
        ))}
        {marked && (
          <span
            aria-hidden
            className="bg-forest-soft text-forest-deep border-forest pointer-events-none absolute top-[10px] flex h-[38px] items-center overflow-hidden whitespace-nowrap rounded-[10px] border-2 border-dashed px-2.5 text-[11px] font-bold"
            style={{ left: `calc(${marked.left}% + 2px)`, width: `calc(${marked.width}% - 4px)` }}
          >
            Auswahl
          </span>
        )}
      </div>
    </div>
  );
}
